/**
 * This code follows the logic of the resize observer
 *  with small adjustments
 */
import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { filter, shareReplay, takeUntil } from 'rxjs/operators';

/* eslint-disable @typescript-eslint/no-non-null-assertion  */

/**
 * A shared IntersectionObserver to be used
 */
class SingleIntersectionObserver {
    /** Stream that emits when the shared observer is destroyed. */
    private readonly _destroyed = new Subject<void>();
    /** Stream of all events from the IntersectionObserver. */
    private readonly _intersectionSubject = new Subject<IntersectionObserverEntry[]>();
    /** IntersectionObserver used to observe element intersection events. */
    private readonly _intersectionObserver?: IntersectionObserver;
    /** A map of elements to streams of their intersection events. */
    private readonly _elementObservables = new Map<Element, Observable<IntersectionObserverEntry[]>>();

    constructor(
        /** The intersection options for the observer */
        _options: IntersectionObserverInit,
    ) {
        if (typeof IntersectionObserver !== 'undefined') {
            this._intersectionObserver = new IntersectionObserver((entries) => this._intersectionSubject.next(entries), _options);
        }
    }

    /**
     * Gets a stream of intersection events for the given element.
     * @param target The element to observe.
     * @return The stream of intersection events for the element.
     */
    observe(target: Element): Observable<IntersectionObserverEntry[]> {
        if (!this._elementObservables.has(target)) {
            this._elementObservables.set(
                target,
                new Observable<IntersectionObserverEntry[]>((observer) => {
                    const subscription = this._intersectionSubject.subscribe(observer);
                    this._intersectionObserver?.observe(target);
                    return () => {
                        this._intersectionObserver?.unobserve(target);
                        subscription.unsubscribe();
                        this._elementObservables.delete(target);
                    };
                }).pipe(
                    filter((entries) => entries.some((entry) => entry.target === target)),
                    // Share a replay of the last event so that subsequent calls to observe the same element
                    // receive initial intersection info like the first one. Also enable ref counting so the
                    // element will be automatically unobserved when there are no more subscriptions.
                    shareReplay({ bufferSize: 1, refCount: true }),
                    takeUntil(this._destroyed),
                ),
            );
        }
        return this._elementObservables.get(target)!;
    }

    /** Destroys this instance. */
    destroy() {
        this._destroyed.next();
        this._destroyed.complete();
        this._intersectionSubject.complete();
        this._elementObservables.clear();
    }
}

/**
 * Allows observing intersection events on multiple elements using a shared IntersectionObserver.
 * Sharing a IntersectionObserver instance is recommended for better performance.
 */
@Injectable({
    providedIn: 'root',
})
export class SharedIntersectionObserver implements OnDestroy {
    /** Map of box type to shared intersection observer. */
    private readonly _observers = new Map<IntersectionObserverInit, SingleIntersectionObserver>();

    ngOnDestroy() {
        for (const [, observer] of this._observers) {
            observer.destroy();
        }
        this._observers.clear();
    }

    /**
     * Gets a stream of intersection events for the given target element and box type.
     * @param target The element to observe for intersections.
     * @param options The init options of the observer
     * @return The stream of intersection events for the element.
     */
    observe(target: Element, options: IntersectionObserverInit): Observable<IntersectionObserverEntry[]> {
        if (!this._observers.has(options)) {
            this._observers.set(options, new SingleIntersectionObserver(options));
        }
        return this._observers.get(options)!.observe(target);
    }
}
